<template>
  <div class="fields-panel">
    <div class="panel-header">
      <h4>{{ t('registration.formFields') }}</h4>
      <button @click="$emit('add')" class="btn btn-primary btn-sm">
        {{ t('registration.addField') }}
      </button>
    </div>

    <div v-if="fields.length === 0" class="empty-fields">
      <h5>{{ t('registration.noFieldsYet') }}</h5>
      <p>{{ t('registration.addFirstField') }}</p>
      <button @click="$emit('add')" class="btn btn-primary">
        {{ t('registration.addFirstField') }}
      </button>
    </div>

    <div v-else class="fields-list">
      <div
        v-for="(field, index) in fields"
        :key="field.id"
        class="field-item"
        :class="{ active: selectedIndex === index }"
        @click="$emit('select', index)"
      >
        <div class="field-header">
          <div class="field-info">
            <span class="field-label">
              {{ field.label || t('registration.unnamedField') }}
            </span>
            <div class="field-meta">
              <span class="field-type">{{
                getFieldTypeLabel(field.type)
              }}</span>
              <span v-if="field.required" class="required-indicator">
                {{ t('general.required') || 'Required' }}
              </span>
            </div>
          </div>
          <div class="field-actions">
            <button
              @click.stop="$emit('move', index, -1)"
              :disabled="index === 0"
              class="btn-icon"
              :title="t('general.moveUp') || 'Move Up'"
            >
              <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 3l-4 4h3v6h2V7h3L8 3z" />
              </svg>
            </button>
            <button
              @click.stop="$emit('move', index, 1)"
              :disabled="index === fields.length - 1"
              class="btn-icon"
              :title="t('general.moveDown') || 'Move Down'"
            >
              <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 13l4-4H9V3H7v6H4l4 4z" />
              </svg>
            </button>
            <button
              @click.stop="$emit('duplicate', index)"
              class="btn-icon"
              :title="t('general.copy') || 'Duplicate'"
            >
              <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M6 2h7v9H6V2zm-3 3h1v8h7v1H3V5zm2 0v5h5V5H5z"
                />
              </svg>
            </button>
            <button
              @click.stop="$emit('remove', index)"
              class="btn-icon btn-danger"
              :title="t('general.delete') || 'Delete'"
            >
              <svg class="icon-svg" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M4.2 3l3.8 3.8L11.8 3 13 4.2 9.2 8l3.8 3.8-1.2 1.2L8 9.2 4.2 13 3 11.8 6.8 8 3 4.2 4.2 3z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'

export default {
  name: 'FieldsList',
  props: {
    fields: { type: Array, required: true },
    selectedIndex: { type: Number, required: true },
  },
  setup() {
    const { t } = useI18n()
    const getFieldTypeLabel = (type) => {
      const map = {
        text: t('registration.fieldTypes.text'),
        email: t('registration.fieldTypes.email'),
        name: t('registration.fieldTypes.name'),
        number: t('registration.fieldTypes.number'),
        textarea: t('registration.fieldTypes.textarea'),
        select: t('registration.fieldTypes.select'),
        radio: t('registration.fieldTypes.radio'),
        checkbox: t('registration.fieldTypes.checkbox'),
        date: t('registration.fieldTypes.date'),
        time: t('registration.fieldTypes.time'),
        section: t('registration.fieldTypes.section'),
      }
      return map[type] || type
    }
    return { t, getFieldTypeLabel }
  },
}
</script>

<style scoped>
.fields-panel {
  background: var(--fb-surface, #ffffff);
  border: 1px solid var(--fb-border, #e4e4e7);
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.65rem 0.9rem;
  background: var(--fb-elevated, #fafafa);
  border-bottom: 1px solid var(--fb-border, #e4e4e7);
}

.panel-header h4 {
  margin: 0;
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 600;
  color: var(--fb-heading, #27272a);
}

.fields-list {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  flex: 1;
  overflow: visible;
}

.field-item {
  background: var(--fb-surface, #fff);
  border: 1px solid var(--fb-border, #e4e4e7);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.field-item:hover {
  border-color: var(--fb-border-strong, #d4d4d8);
  background-color: var(--fb-hover-fill, #e4e4e7);
}

.field-item.active {
  border-color: var(--fb-item-selected-border, #a1a1aa);
  background-color: var(--fb-item-selected-bg, #e4e4e7);
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.field-info {
  flex: 1;
}
.field-label {
  font-weight: 600;
  color: var(--fb-heading, #27272a);
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
}
.field-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}
.field-type {
  font-size: 0.72rem;
  color: var(--fb-muted, #52525b);
  text-transform: none;
  font-weight: 500;
  background: var(--fb-code-bg, #e4e4e7);
  border: 1px solid var(--fb-border, #e4e4e7);
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
}
.required-indicator {
  color: var(--fb-danger, #b91c1c);
  font-size: 0.72rem;
  font-weight: 600;
  background: var(--fb-danger-soft, #fef2f2);
  border: 1px solid var(--fb-danger, #b91c1c);
  padding: 0.12rem 0.45rem;
  border-radius: 6px;
}
.field-actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.btn-icon {
  background: var(--fb-elevated, #fafafa);
  border: 1px solid var(--fb-border, #e4e4e7);
  cursor: pointer;
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: var(--fb-muted, #52525b);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}
.btn-icon:hover:not(:disabled) {
  background-color: var(--fb-hover-fill, #e4e4e7);
  border-color: var(--fb-border-strong, #d4d4d8);
  color: var(--fb-heading, #27272a);
}
.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-svg {
  width: 15px;
  height: 15px;
  fill: currentColor;
  display: block;
}
.btn-icon.btn-danger {
  color: var(--fb-danger, #b91c1c);
  border-color: var(--fb-border, #e4e4e7);
}
.btn-icon.btn-danger:hover {
  background-color: var(--fb-danger-soft, #fef2f2);
  color: var(--fb-danger-hover, #991b1b);
}

.btn {
  padding: 0.45rem 0.85rem;
  border: none;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.btn-sm {
  padding: 0.35rem 0.65rem;
  font-size: 0.78rem;
}
.btn-primary {
  background-color: var(--fb-primary, #3f3f46);
  color: #fafaf9;
}
.btn-primary:hover {
  background-color: var(--fb-primary-hover, #27272a);
}

.empty-fields {
  text-align: center;
  padding: 1.5rem 1rem;
  color: var(--fb-muted, #52525b);
}

.empty-fields h5 {
  margin: 0 0 0.35rem 0;
  color: var(--fb-heading, #27272a);
  font-size: 1rem;
  font-weight: 600;
}

.empty-fields p {
  margin: 0 0 0.9rem 0;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .panel-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .panel-header .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
